import type { Attributes, InheritAttributesObserver } from './types';

/**
 * This helper function is used to inherit attributes and at the same time watch
 * for changes via a mutation observer
 */
export function createInheritAttributesObserver(el: HTMLElement, attributes: Array<string>, cb: (attributes: Attributes) => void): InheritAttributesObserver {
    const observer = new MutationObserver(mutations => {
      const changedAttributeNames = mutations.filter(mutation => mutation.type === 'attributes' && mutation.attributeName).map(mutation => mutation.attributeName!);
      if (changedAttributeNames.length) {
        // Disconnect to skip the next attribute mutation
        observer.disconnect()
        // Inherit the changed attributes
        const inheritedAttributes = inheritAttributes(el, changedAttributeNames)
        // Start the observer again
        observe();
        // Call the callback function
        cb(inheritedAttributes)
      }
    })

    function observe() {
      observer.observe(el, {attributes: true, attributeFilter: attributes})
    }

    function start() {
      const inheritedAttributes = inheritAttributes(el, attributes);
      observe();
      cb(inheritedAttributes);
    }

    function destroy() {
      observer.disconnect();
    }

    /**
     * When using a controller overlay you can set attributes
     * using the htmlAttributes property. Since the above attributes
     * need to be inherited inside the overlay, we need to look
     * and see if these attributes are being set via htmlAttributes.
     */
    function mergeWithHtmlAttributes(inheritedAttributes: Attributes, htmlAttributes: { [key: string]: unknown }) {
      if (htmlAttributes !== undefined) {
        attributes.forEach((attribute) => {
          const attributeValue = htmlAttributes[attribute];
          if (attributeValue) {
            /**
             * If an attribute we need to inherit was
             * set using htmlAttributes then add it to
             * inheritedAttributes and remove it from htmlAttributes.
             * This ensures the attribute is inherited and not
             * set on the host.
             *
             * In this case, if an inherited attribute is set
             * on the host element and using htmlAttributes then
             * htmlAttributes wins, but that's not a pattern that we recommend.
             * The only time you'd need htmlAttributes is when using modalController.
             */
            inheritedAttributes[attribute] = htmlAttributes[attribute];

            delete htmlAttributes[attribute];
          }
        });
      }
    }

    return {
      start,
      destroy,
      mergeWithHtmlAttributes
    }
}

/**
 * List of available ARIA attributes + `role`.
 * Removed deprecated attributes.
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes
 */
const ariaAttributesList = [
  'role',
  'aria-activedescendant',
  'aria-atomic',
  'aria-autocomplete',
  'aria-braillelabel',
  'aria-brailleroledescription',
  'aria-busy',
  'aria-checked',
  'aria-colcount',
  'aria-colindex',
  'aria-colindextext',
  'aria-colspan',
  'aria-controls',
  'aria-current',
  'aria-describedby',
  'aria-description',
  'aria-details',
  'aria-disabled',
  'aria-errormessage',
  'aria-expanded',
  'aria-flowto',
  'aria-haspopup',
  'aria-hidden',
  'aria-invalid',
  'aria-keyshortcuts',
  'aria-label',
  'aria-labelledby',
  'aria-level',
  'aria-live',
  'aria-multiline',
  'aria-multiselectable',
  'aria-orientation',
  'aria-owns',
  'aria-placeholder',
  'aria-posinset',
  'aria-pressed',
  'aria-readonly',
  'aria-relevant',
  'aria-required',
  'aria-roledescription',
  'aria-rowcount',
  'aria-rowindex',
  'aria-rowindextext',
  'aria-rowspan',
  'aria-selected',
  'aria-setsize',
  'aria-sort',
  'aria-valuemax',
  'aria-valuemin',
  'aria-valuenow',
  'aria-valuetext',
];

export function ariaAttributes(ignoreList?: Array<string>) {
  return ariaAttributesList.filter(attribute => !ignoreList || !ignoreList.includes(attribute));
}

/**
 * This helper function checks attributes on an element, removes them if they exist
 * and returns an object ob the values
 */
function inheritAttributes(el: HTMLElement, attributes: Array<string> = []) {
  const attributeObject: Attributes = {};

  attributes.forEach((attr) => {
    if (el.hasAttribute(attr)) {
      const value = el.getAttribute(attr);
      if (value !== null) {
        attributeObject[attr] = el.getAttribute(attr);
      }
      el.removeAttribute(attr);
    }
  });

  return attributeObject;
}
